import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import {
  OFFLINE_DURABLE_GC_TIME,
  recordKeys,
  type RecordFilters,
} from "@/lib/query-keys";
import { filterRecords, hasActiveRecordFilter } from "@/lib/records/filter-records";
import { useOnlineStatus } from "@/offline/online-status";
import { isPinnedSync } from "@/offline/pins";
import { RECORDS_PAGES } from "@/offline/sync";
import { normalizeRecordsPage } from "@/offline/record-shape";
import type { PaginatedResponse, ProjectRecord } from "@/types/api";

const PAGE_SIZE = 100;

export function buildQueryString(page: number, filters: RecordFilters): string {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("page_size", String(PAGE_SIZE));
  for (const tag of filters.tags ?? []) params.append("tags", tag);
  // `created_by` is a comma-separated list — the backend `UUIDInFilter`
  // expects one value, not repeated params (unlike `tags` above).
  if (filters.created_by?.length) {
    params.set("created_by", filters.created_by.join(","));
  }
  // `plan` is a comma-separated list — the backend expects one value
  // (OR across plan IDs), unlike `tags` which is repeated.
  if (filters.plan?.length) {
    params.set("plan", filters.plan.join(","));
  }
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.has_pin != null) params.set("has_pin", String(filters.has_pin));
  if (filters.search) params.set("search", filters.search);
  if (filters.hasTags === false) params.set("has_tags", "false");
  if (filters.hasTags === true) params.set("has_tags", "true");
  return params.toString();
}

/**
 * Build the `getNextPageParam` callback for `useInfiniteQuery`.
 *
 * While the project is pinned and we're offline, only the first
 * `RECORDS_PAGES` pages are prefetched into the persister cache. Page
 * `RECORDS_PAGES + 1` has no cached data, so attempting to load it would
 * fail offline. Stop pagination gracefully at the cache boundary — the
 * grid renders a "{cached} of {total} photos available offline" footer
 * instead of an error.
 *
 * Extracted to a named helper so the offline-cap branch is unit-testable
 * without driving a full `useInfiniteQuery` render. The hook still uses
 * this same helper at runtime.
 */
export function buildGetNextPageParam(isPinnedAndOffline: boolean) {
  return (
    lastPage: PaginatedResponse<ProjectRecord>,
    allPages: PaginatedResponse<ProjectRecord>[],
    lastPageParam: number,
  ): number | undefined => {
    if (isPinnedAndOffline && allPages.length >= RECORDS_PAGES) return undefined;
    return lastPage.next ? lastPageParam + 1 : undefined;
  };
}

export interface ProjectRecordsResult {
  data: { records: ProjectRecord[]; totalCount: number } | undefined;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** True when the project is pinned, the client is offline, and all
   * `RECORDS_PAGES` worth of cached pages have been loaded. PhotoGrid uses
   * this to render the "{cached} of {total} photos available offline"
   * footer instead of trying (and failing) to fetch page `RECORDS_PAGES+1`. */
  offlineCapReached: boolean;
  /** Total records loaded across all pages — equivalent to
   * `data.records.length`, but computed from the raw pages so it's
   * available even when callers don't need the flattened `records` array. */
  cachedCount: number;
  /** True when offline AND a tag/date/plan filter is active — the grid is
   * filtered client-side over the cached records, not the full project. */
  isFilteredOffline: boolean;
}

export function useProjectRecords(
  projectId: string,
  filters: RecordFilters = {},
): ProjectRecordsResult {
  const fetchWithAuth = useAuthenticatedFetch();
  const isOnline = useOnlineStatus();
  const isPinned = isPinnedSync(projectId);
  const isPinnedAndOffline = !isOnline && isPinned;
  // Offline, the per-filter server query has no cached data — only the
  // unfiltered list is persisted. Point the query at that cached `{}` key
  // and filter client-side in the `data` memo below. Online is unchanged.
  const effectiveFilters: RecordFilters = isPinnedAndOffline ? {} : filters;

  const query = useInfiniteQuery({
    queryKey: recordKeys.list(projectId, effectiveFilters),
    queryFn: async ({ pageParam }) =>
      // Normalize the v2.0 `images[]` wire (`*_url` only) to the canonical
      // `{ image, thumbnail }` shape the grid + cache read. See record-shape.
      normalizeRecordsPage(
        await fetchWithAuth<PaginatedResponse<ProjectRecord>>(
          `/projects/2.0/projects/${projectId}/records/?${buildQueryString(
            pageParam,
            effectiveFilters,
          )}`,
        ),
      ),
    initialPageParam: 1,
    getNextPageParam: buildGetNextPageParam(isPinnedAndOffline),
    staleTime: 30_000,
    // GC-immune only while pinned — mirrors what the persister saves for this
    // project. Non-pinned projects keep the default gcTime.
    gcTime: isPinned ? OFFLINE_DURABLE_GC_TIME : undefined,
  });

  // Flatten pages into the existing `{ records, totalCount }` shape that
  // callers expect. Inlined here (rather than via `select`) so we also have
  // access to `query.data.pages` for the offline-cap and cached-count
  // computations below.
  const data = useMemo(() => {
    if (!query.data) return undefined;
    const raw = query.data.pages.flatMap((page) => page.results);
    return {
      // Offline, the query holds the unfiltered cached list — narrow it
      // client-side. Online, the server already applied the filters.
      records: isPinnedAndOffline ? filterRecords(raw, filters) : raw,
      totalCount: query.data.pages[0]?.count ?? 0,
    };
  }, [query.data, isPinnedAndOffline, filters]);

  const offlineCapReached =
    isPinnedAndOffline && (query.data?.pages.length ?? 0) >= RECORDS_PAGES;
  const cachedCount =
    query.data?.pages.reduce((sum, page) => sum + page.results.length, 0) ?? 0;
  const isFilteredOffline =
    isPinnedAndOffline && hasActiveRecordFilter(filters);

  return {
    data,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
    offlineCapReached,
    cachedCount,
    isFilteredOffline,
  };
}
